/**
 * Port of lib/openai_service.py.
 *
 * Python used the official OpenAI SDK (Responses + Conversations APIs). The static browser
 * build uses direct `fetch` calls against https://api.openai.com instead (no SDK package):
 *   - POST /v1/responses      — every JSON-schema call (client.responses.create)
 *   - POST /v1/conversations  — conversation creation (client.conversations.create)
 *
 * BYOK key resolution (replaces Python's OPENAI_API_KEY env var), in order:
 *   1. a runtime key installed via `set_openai_api_key()` — fed at startup by the Access API
 *      (secrets_boot.ts), or by a sidebar BYOK override,
 *   2. the storage backend at "settings/openai_api_key",
 *   3. absent → the exact no-key behaviour of the Python module without OPENAI_API_KEY:
 *      every call returns (null, ..., "OPENAI_API_KEY is not set.") and callers degrade
 *      gracefully (the deterministic engine keeps working without AI assists).
 *
 * The optional vector store id (Python: OPENAI_VECTOR_STORE_ID env var) follows the same
 * runtime-setter / storage pattern at "settings/openai_vector_store_id".
 *
 * All call functions are async (fetch). The HTTP layer is a single seam, `_post_openai()`,
 * whose fetch implementation tests can stub via `set_fetch_impl()`.
 */

import { EVENT_CATEGORIES, load_openai_config, load_openai_settings, model_pricing } from "./config";
import { load_instruction, load_schema } from "./instructions";
import { append_log, append_verbose_log, normalize_event } from "./json_store";
import { pythonRound } from "./pyutil";
import { getStorage } from "./storage";

// Loaded from the editable instructions/extraction_fact_event.json schema. The event `category`
// enum is injected from the live EVENT_CATEGORIES catalog (so it can never drift from the engine).
export const FACT_EXTRACTION_SCHEMA: Record<string, any> = load_schema("extraction_fact_event.json", {
  event_categories: EVENT_CATEGORIES,
});

export function compact_events_for_openai(events: Record<string, any>[]): Record<string, any>[] {
  return events.filter(_is_plain_object).map((item) => normalize_event(item));
}

function _is_plain_object(value: any): value is Record<string, any> {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/**
 * The model's text output from a raw /v1/responses payload.
 *
 * The SDK exposed a computed `output_text` property; the raw REST response does not carry
 * one, so this aggregates every `output_text` content part of every `message` output item
 * — exactly how the SDK computes it. A literal `output_text` field (e.g. from a test stub
 * or a future API) still takes precedence, mirroring the Python attribute/dict check.
 */
export function _response_output_text(response: any): string {
  if (!_is_plain_object(response)) return "";
  const direct = response["output_text"];
  if (direct) return String(direct);
  const parts: string[] = [];
  for (const item of Array.isArray(response["output"]) ? response["output"] : []) {
    if (!_is_plain_object(item) || item["type"] !== "message") continue;
    for (const content of Array.isArray(item["content"]) ? item["content"] : []) {
      if (_is_plain_object(content) && content["type"] === "output_text" && typeof content["text"] === "string") {
        parts.push(content["text"]);
      }
    }
  }
  return parts.join("");
}

/** Python _jsonable: fetch responses are already plain JSON, so this is a pass-through deep copy guard. */
export function _jsonable(value: any): any {
  if (Array.isArray(value)) return value.map((item) => _jsonable(item));
  if (_is_plain_object(value)) {
    const out: Record<string, any> = {};
    for (const [key, item] of Object.entries(value)) {
      if (!key.startsWith("_")) out[key] = _jsonable(item);
    }
    return out;
  }
  return value;
}

function _usage_value(usage: any, key: string): number {
  if (usage === null || usage === undefined) return 0;
  if (_is_plain_object(usage)) return Math.trunc(Number(usage[key] ?? 0) || 0);
  return 0;
}

/**
 * Cached (prompt-cache hit) input tokens for this response. The Responses API reports them at
 * ``usage.input_tokens_details.cached_tokens``; the Chat Completions shape
 * (``prompt_tokens_details``) and a flat ``cached_tokens`` are accepted too. These tokens are
 * INCLUDED in ``input_tokens`` but billed at the discounted cached rate.
 */
function _cached_input_tokens(usage: any): number {
  if (!_is_plain_object(usage)) return 0;
  for (const container of ["input_tokens_details", "prompt_tokens_details"]) {
    const details = usage[container];
    if (_is_plain_object(details) && details["cached_tokens"] !== undefined) {
      return Math.max(0, Math.trunc(Number(details["cached_tokens"] ?? 0) || 0));
    }
  }
  return Math.max(0, _usage_value(usage, "cached_tokens"));
}

const _round8 = (x: number) => pythonRound(x, 8);

/**
 * Token usage + cost for one response. Per-1M-token rates come from the model's entry in
 * ``openai.json``'s root ``models`` map (keyed by the model actually used for this call), falling
 * back to the OPENAI_*_COST_PER_1M_TOKENS env vars.
 *
 * Input tokens are split into CACHED (prompt-cache hits, billed at the discounted cached rate) and
 * uncached; ``input_cost`` is the sum of both halves, so ``total_cost = input_cost + output_cost``
 * still holds.
 */
export function openai_cost_summary(response: any, model: string | null = null): Record<string, any> {
  const usage = _is_plain_object(response) ? response["usage"] : undefined;
  const input_tokens = _usage_value(usage, "input_tokens");
  const output_tokens = _usage_value(usage, "output_tokens");
  const total_tokens = _usage_value(usage, "total_tokens") || input_tokens + output_tokens;
  // Cached tokens are a SUBSET of input_tokens — clamp so a malformed usage block can never
  // produce a negative uncached count (and therefore a negative cost).
  const cached_input_tokens = Math.min(_cached_input_tokens(usage), input_tokens);
  const uncached_input_tokens = input_tokens - cached_input_tokens;
  const pricing = model_pricing(model);
  const input_rate = pricing["input_cost_per_1m"];
  const cached_input_rate = pricing["cached_input_cost_per_1m"];
  const output_rate = pricing["output_cost_per_1m"];
  const uncached_input_cost = (uncached_input_tokens * input_rate) / 1_000_000;
  const cached_input_cost = (cached_input_tokens * cached_input_rate) / 1_000_000;
  const input_cost = uncached_input_cost + cached_input_cost;
  const output_cost = (output_tokens * output_rate) / 1_000_000;
  return {
    model,
    input_tokens,
    cached_input_tokens,
    uncached_input_tokens,
    output_tokens,
    total_tokens,
    input_cost_per_1m_tokens: input_rate,
    cached_input_cost_per_1m_tokens: cached_input_rate,
    output_cost_per_1m_tokens: output_rate,
    uncached_input_cost: _round8(uncached_input_cost),
    cached_input_cost: _round8(cached_input_cost),
    input_cost: _round8(input_cost),
    output_cost: _round8(output_cost),
    total_cost: _round8(input_cost + output_cost),
    usage_raw: _jsonable(usage),
    cost_note:
      "Per-model rates come from openai.json -> models[<model>]; falls back to the " +
      "OPENAI_INPUT_COST_PER_1M_TOKENS / OPENAI_CACHED_INPUT_COST_PER_1M_TOKENS / " +
      "OPENAI_OUTPUT_COST_PER_1M_TOKENS env seam (set_env). Cached input tokens are a subset of " +
      "input tokens, billed at the cached rate.",
  };
}

/** A zeroed running-total block (the shape stored at run_data.ai_cost_total). */
export function empty_ai_cost_total(): Record<string, any> {
  return {
    input_tokens: 0,
    cached_input_tokens: 0,
    uncached_input_tokens: 0,
    output_tokens: 0,
    total_tokens: 0,
    input_cost: 0.0,
    cached_input_cost: 0.0,
    output_cost: 0.0,
    total_cost: 0.0,
    calls: 0,
    by_schema: {},
    updated_at: null,
  };
}

const _COST_TOKEN_KEYS = ["input_tokens", "cached_input_tokens", "uncached_input_tokens", "output_tokens", "total_tokens"];
const _COST_MONEY_KEYS = ["input_cost", "cached_input_cost", "output_cost", "total_cost"];

export function log_openai_cost(run_data: Record<string, any> | null, schema_name: string, cost: Record<string, any>): void {
  if (run_data === null || run_data === undefined) return;
  if (!_is_plain_object(run_data["ai_cost_total"])) {
    run_data["ai_cost_total"] = empty_ai_cost_total();
  }
  const totals = run_data["ai_cost_total"];
  for (const key of _COST_TOKEN_KEYS) {
    totals[key] = Math.trunc(Number(totals[key] ?? 0)) + Math.trunc(Number(cost[key] ?? 0));
  }
  for (const key of _COST_MONEY_KEYS) {
    totals[key] = _round8(Number(totals[key] ?? 0.0) + Number(cost[key] ?? 0.0));
  }
  totals["calls"] = Math.trunc(Number(totals["calls"] ?? 0)) + 1;
  totals["updated_at"] = new Date().toISOString().replace(/\.\d+Z$/, "Z");
  if (!_is_plain_object(totals["by_schema"])) totals["by_schema"] = {};
  if (!_is_plain_object(totals["by_schema"][schema_name])) {
    totals["by_schema"][schema_name] = {
      input_tokens: 0,
      cached_input_tokens: 0,
      output_tokens: 0,
      total_tokens: 0,
      total_cost: 0.0,
      calls: 0,
    };
  }
  const schema_total = totals["by_schema"][schema_name];
  for (const key of ["input_tokens", "cached_input_tokens", "output_tokens", "total_tokens"]) {
    schema_total[key] = Math.trunc(Number(schema_total[key] ?? 0)) + Math.trunc(Number(cost[key] ?? 0));
  }
  schema_total["total_cost"] = _round8(Number(schema_total["total_cost"] ?? 0.0) + Number(cost["total_cost"] ?? 0.0));
  schema_total["model"] = cost["model"] ?? schema_total["model"] ?? null;
  schema_total["calls"] = Math.trunc(Number(schema_total["calls"] ?? 0)) + 1;
  const cached_note = Number(cost["cached_input_tokens"] ?? 0)
    ? `, ${cost["cached_input_tokens"]} of them cached`
    : "";
  append_log(
    run_data as any,
    `OpenAI ${schema_name} cost estimate: $${Number(cost["total_cost"]).toFixed(6)} ` +
      `(${cost["input_tokens"]} input tokens${cached_note}, ${cost["output_tokens"]} output tokens).`,
  );
}

/**
 * Carry the AI spend accumulated on a DETACHED run clone back onto the live run.
 *
 * The chat/vision call sites hand `structuredClone(run_data)` to the OpenAI layer (so a failed
 * turn cannot half-write the real run) and then copy the pieces they want back inside `mutate`.
 * Without this the whole turn's cost was silently discarded. The clone started from the live
 * totals, so the higher `calls` count is the newer one — an assignment, not an addition (a stale
 * clone from before a concurrent turn is simply ignored).
 */
export function carry_ai_cost(target_run: Record<string, any>, source_run: any): void {
  if (!_is_plain_object(target_run) || !_is_plain_object(source_run)) return;
  const source = source_run["ai_cost_total"];
  if (!_is_plain_object(source)) return;
  const current = _is_plain_object(target_run["ai_cost_total"]) ? target_run["ai_cost_total"] : null;
  if (current && Math.trunc(Number(current["calls"] ?? 0)) >= Math.trunc(Number(source["calls"] ?? 0))) return;
  target_run["ai_cost_total"] = source;
}

// ── BYOK key + vector store resolution (replaces os.environ) ───────────────────────────────

/** Storage path of the user-supplied OpenAI API key (BYOK). */
export const OPENAI_API_KEY_PATH = "settings/openai_api_key";
/** Storage path of the optional vector store id (Python: OPENAI_VECTOR_STORE_ID). */
export const OPENAI_VECTOR_STORE_ID_PATH = "settings/openai_vector_store_id";

let _runtime_api_key: string | null = null;
let _runtime_vector_store_id: string | null = null;

/** Install (or clear, with null) the runtime OpenAI API key. Highest-priority source. */
export function set_openai_api_key(key: string | null): void {
  _runtime_api_key = key && key.trim() ? key.trim() : null;
}

/** Install (or clear, with null) the runtime vector store id. Highest-priority source. */
export function set_openai_vector_store_id(id: string | null): void {
  _runtime_vector_store_id = id && id.trim() ? id.trim() : null;
}

/**
 * Resolved API key, in priority order: sidebar/runtime override, then a per-browser stored key.
 * The key is NO LONGER bundled — it is fetched from the Access API at startup and installed via
 * `set_openai_api_key` (see secrets_boot.ts); a sidebar BYOK field still overrides it. Null → "not
 * set" (the engine degrades gracefully, exactly as it did with no OPENAI_API_KEY).
 */
export function get_openai_api_key(): string | null {
  if (_runtime_api_key) return _runtime_api_key;
  const stored = getStorage().read(OPENAI_API_KEY_PATH);
  if (stored && stored.trim()) return stored.trim();
  return null;
}

function _get_vector_store_id(): string | null {
  if (_runtime_vector_store_id) return _runtime_vector_store_id;
  const stored = getStorage().read(OPENAI_VECTOR_STORE_ID_PATH);
  if (stored && stored.trim()) return stored.trim();
  const hardcoded = load_openai_config()["vector_store_id"];
  return typeof hardcoded === "string" && hardcoded.trim() ? hardcoded.trim() : null;
}

// ── HTTP seam (replaces the SDK client; tests stub set_fetch_impl) ─────────────────────────

export const OPENAI_BASE_URL = "https://api.openai.com";
/** Python's OpenAI(timeout=120.0). */
const _REQUEST_TIMEOUT_MS = 120_000;
/** Streaming read-inactivity watchdog: abort when no chunk has arrived for this long. */
const _STREAM_INACTIVITY_TIMEOUT_MS = 60_000;
/**
 * Streaming overall deadline. Deliberately much longer than _REQUEST_TIMEOUT_MS: a healthy
 * reasoning+file_search turn can legitimately stream well past 120s, and aborting an ACTIVE
 * stream truncates the turn (recovery finds it still in_progress and gives up). True stalls
 * are the inactivity watchdog's job; this is only a backstop against an endless stream.
 */
const _STREAM_MAX_DURATION_MS = 10 * 60_000;
/** The SDK's default max_retries=2 on connection errors / 408 / 429 / 5xx. */
const _MAX_RETRIES = 2;

type FetchImpl = (url: string, init: Record<string, any>) => Promise<{
  ok: boolean;
  status: number;
  text(): Promise<string>;
}>;

let _fetch_impl: FetchImpl | null = null;

/** Install (or clear, with null) a fetch replacement — the test seam for every OpenAI call. */
export function set_fetch_impl(impl: FetchImpl | null): void {
  _fetch_impl = impl;
}

function _active_fetch(): FetchImpl {
  if (_fetch_impl) return _fetch_impl;
  if (typeof fetch === "undefined") {
    throw new Error("fetch is not available in this environment");
  }
  return fetch as unknown as FetchImpl;
}

function _should_retry_status(status: number): boolean {
  return status === 408 || status === 429 || status >= 500;
}

/** Backoff before retry N (0-based): 500ms then 2s — a zero-delay retry against a rate-limited
 * or overloaded API just burns the attempt. */
const _RETRY_BACKOFF_MS = [500, 2000];

/**
 * Sleep before the next retry attempt, honoring a Retry-After header (seconds, capped at 30s)
 * when the server sent one. Skipped entirely when the test fetch seam is installed so unit
 * tests never sleep.
 */
async function _retry_sleep(attempt: number, retry_after_header: string | null): Promise<void> {
  if (_fetch_impl) return;
  let ms = _RETRY_BACKOFF_MS[Math.min(attempt, _RETRY_BACKOFF_MS.length - 1)];
  const retry_after = retry_after_header !== null ? Number(retry_after_header) : NaN;
  if (Number.isFinite(retry_after) && retry_after > 0) {
    ms = Math.min(retry_after * 1000, 30_000);
  }
  await new Promise((resolve) => setTimeout(resolve, ms));
}

/**
 * One POST to the OpenAI REST API. Returns the parsed JSON body; throws on HTTP errors
 * (after the SDK-equivalent retry policy: up to 2 retries on connection errors/408/429/5xx,
 * with a short exponential backoff between attempts).
 */
export async function _post_openai(path: string, body: Record<string, any>, api_key: string): Promise<Record<string, any>> {
  const url = `${OPENAI_BASE_URL}${path}`;
  let last_error: any = null;
  for (let attempt = 0; attempt <= _MAX_RETRIES; attempt++) {
    let response: Awaited<ReturnType<FetchImpl>>;
    try {
      response = await _active_fetch()(url, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${api_key}`,
          "Content-Type": "application/json",
        },
        body: JSON.stringify(body),
        signal: typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(_REQUEST_TIMEOUT_MS) : undefined,
      });
    } catch (exc) {
      // Connection-level failure — retry like the SDK does.
      last_error = exc;
      if (attempt < _MAX_RETRIES) await _retry_sleep(attempt, null);
      continue;
    }
    const text = await response.text();
    if (response.ok) {
      try {
        return JSON.parse(text);
      } catch (exc) {
        throw new Error(`OpenAI returned non-JSON body: ${exc}`);
      }
    }
    last_error = new Error(`OpenAI API error ${response.status}: ${text}`);
    if (!_should_retry_status(response.status)) throw last_error;
    if (attempt < _MAX_RETRIES) {
      // Real fetch Responses carry headers; the test seam's minimal shape may not.
      const retry_after = (response as any).headers?.get?.("retry-after") ?? null;
      await _retry_sleep(attempt, typeof retry_after === "string" ? retry_after : null);
    }
  }
  throw last_error instanceof Error ? last_error : new Error(String(last_error));
}

function _error_text(exc: any): string {
  return exc instanceof Error ? exc.message : String(exc);
}

/**
 * One GET to the OpenAI REST API (used to retrieve a response whose stream broke client-side).
 * Throws an Error whose `status` carries the HTTP code so the poller can stop on fatal codes.
 */
async function _get_openai(path: string, api_key: string): Promise<Record<string, any>> {
  const response = await _active_fetch()(`${OPENAI_BASE_URL}${path}`, {
    method: "GET",
    headers: { Authorization: `Bearer ${api_key}` },
    signal: typeof AbortSignal !== "undefined" && "timeout" in AbortSignal ? AbortSignal.timeout(_REQUEST_TIMEOUT_MS) : undefined,
  });
  const text = await response.text();
  if (!response.ok) {
    const err = new Error(`OpenAI API error ${response.status}: ${text}`) as Error & { status?: number };
    err.status = response.status;
    throw err;
  }
  return JSON.parse(text);
}

/** Between-poll waits for stream recovery (~7s total across the gaps — the model is usually done). */
const _STREAM_RECOVERY_DELAYS_MS = [400, 800, 1600, 3200];

/**
 * A streamed response whose SSE connection broke client-side ("Error in input stream") has
 * usually still COMPLETED on OpenAI's side — the turn exists, we just lost the tail of the feed.
 * Retrieve it by id (GET /v1/responses/{id}), polling briefly while it is still in progress, so
 * the user's turn is saved instead of failed. Null when it truly failed/can't be fetched. Stops
 * early on a fatal HTTP status (auth/permission) — retrying those can't help.
 */
export async function _recover_streamed_response(
  response_id: string,
  api_key: string,
): Promise<Record<string, any> | null> {
  // One attempt per iteration; only SLEEP between attempts, never after the last (no wasted tail).
  for (let attempt = 0; ; attempt++) {
    try {
      const resp = await _get_openai(`/v1/responses/${response_id}`, api_key);
      const status = String(resp?.["status"] ?? "");
      if (status === "completed" || status === "incomplete") return resp;
      if (status === "failed" || status === "cancelled") return null;
      // queued / in_progress → keep waiting
    } catch (exc) {
      const code = (exc as { status?: number })?.status;
      if (code === 401 || code === 403 || code === 404) return null; // fatal — polling won't help
      /* transient fetch/5xx error — keep polling */
    }
    if (attempt >= _STREAM_RECOVERY_DELAYS_MS.length) return null;
    await new Promise((r) => setTimeout(r, _STREAM_RECOVERY_DELAYS_MS[attempt]));
  }
}

// ── JSON-schema call functions (Python's call_openai_*; all async via fetch) ───────────────

export async function call_openai_json(
  schema_name: string,
  schema: Record<string, any>,
  system_prompt: string,
  payload: Record<string, any>,
  log_slug: string | null = null,
  run_data: Record<string, any> | null = null,
): Promise<[Record<string, any> | null, string | null, string | null]> {
  const api_key = get_openai_api_key();
  const vector_store_id = _get_vector_store_id();
  const settings = load_openai_settings(schema_name);
  const model = settings["model"];
  const reasoning = settings["reasoning"];
  const tools = vector_store_id ? [{ type: "file_search", vector_store_ids: [vector_store_id] }] : [];
  const request_log = {
    schema_name,
    model,
    reasoning,
    vector_store_id,
    system_prompt,
    payload,
    json_schema: schema,
    tools,
  };
  if (log_slug) {
    append_verbose_log(log_slug, `openai.request.${schema_name}`, request_log);
  }
  if (!api_key) {
    if (log_slug) {
      append_verbose_log(log_slug, `openai.skipped.${schema_name}`, { error: "OPENAI_API_KEY is not set.", request: request_log });
    }
    return [null, null, "OPENAI_API_KEY is not set."];
  }

  try {
    const create_kwargs: Record<string, any> = {
      model,
      input: [
        { role: "system", content: system_prompt },
        { role: "user", content: JSON.stringify(payload) },
      ],
      text: {
        format: {
          type: "json_schema",
          name: schema_name,
          schema,
          strict: true,
        },
      },
    };
    if (tools.length) create_kwargs["tools"] = tools;
    if (reasoning) create_kwargs["reasoning"] = reasoning;
    const response = await _post_openai("/v1/responses", create_kwargs, api_key);
    const raw_text = _response_output_text(response);
    const cost = openai_cost_summary(response, model);
    log_openai_cost(run_data, schema_name, cost);
    if (!raw_text) {
      if (log_slug) {
        append_verbose_log(log_slug, `openai.response.${schema_name}`, {
          response_id: response["id"] ?? null,
          raw_text,
          cost,
          raw_response: _jsonable(response),
          error: "OpenAI returned an empty response.",
        });
      }
      return [null, response["id"] ?? null, "OpenAI returned an empty response."];
    }
    const parsed = JSON.parse(raw_text);
    if (log_slug) {
      append_verbose_log(log_slug, `openai.response.${schema_name}`, {
        response_id: response["id"] ?? null,
        raw_text,
        parsed_json: parsed,
        cost,
        raw_response: _jsonable(response),
      });
    }
    return [parsed, response["id"] ?? null, null];
  } catch (exc) {
    if (log_slug) {
      append_verbose_log(log_slug, `openai.error.${schema_name}`, { error: _error_text(exc), request: request_log });
    }
    return [null, null, `OpenAI call failed: ${_error_text(exc)}`];
  }
}

/**
 * Like call_openai_json, but the user turn carries one or more images (data URLs) for a
 * vision-capable model. Returns (parsed_json, response_id, error).
 */
export async function call_openai_vision_json(
  schema_name: string,
  schema: Record<string, any>,
  system_prompt: string,
  user_text: string,
  image_data_urls: string[],
  log_slug: string | null = null,
  run_data: Record<string, any> | null = null,
): Promise<[Record<string, any> | null, string | null, string | null]> {
  const api_key = get_openai_api_key();
  const settings = load_openai_settings(schema_name);
  const model = settings["model"];
  const reasoning = settings["reasoning"];
  const request_log = {
    schema_name,
    model,
    reasoning,
    system_prompt,
    user_text,
    image_count: image_data_urls.length,
    json_schema: schema,
  };
  if (log_slug) {
    append_verbose_log(log_slug, `openai.request.${schema_name}`, request_log);
  }
  if (!api_key) {
    return [null, null, "OPENAI_API_KEY is not set."];
  }
  try {
    const content: Record<string, any>[] = [{ type: "input_text", text: user_text }];
    for (const url of image_data_urls) {
      content.push({ type: "input_image", image_url: url });
    }
    const create_kwargs: Record<string, any> = {
      model,
      input: [
        { role: "system", content: system_prompt },
        { role: "user", content },
      ],
      text: { format: { type: "json_schema", name: schema_name, schema, strict: true } },
    };
    if (reasoning) create_kwargs["reasoning"] = reasoning;
    const response = await _post_openai("/v1/responses", create_kwargs, api_key);
    const raw_text = _response_output_text(response);
    const cost = openai_cost_summary(response, model);
    log_openai_cost(run_data, schema_name, cost);
    if (!raw_text) {
      return [null, response["id"] ?? null, "OpenAI returned an empty response."];
    }
    const parsed = JSON.parse(raw_text);
    if (log_slug) {
      append_verbose_log(log_slug, `openai.response.${schema_name}`, {
        response_id: response["id"] ?? null,
        parsed_json: parsed,
        cost,
      });
    }
    return [parsed, response["id"] ?? null, null];
  } catch (exc) {
    if (log_slug) {
      append_verbose_log(log_slug, `openai.error.${schema_name}`, { error: _error_text(exc), request: request_log });
    }
    return [null, null, `OpenAI vision call failed: ${_error_text(exc)}`];
  }
}

/**
 * Create a new OpenAI Conversation and return (conversation_id, error). The server then keeps
 * the message history for us — we never resend it.
 */
export async function ensure_openai_conversation(log_slug: string | null = null): Promise<[string | null, string | null]> {
  const api_key = get_openai_api_key();
  if (!api_key) {
    return [null, "OPENAI_API_KEY is not set."];
  }
  try {
    const conversation = await _post_openai("/v1/conversations", {}, api_key);
    return [conversation["id"], null];
  } catch (exc) {
    if (log_slug) {
      append_verbose_log(log_slug, "openai.error.conversation_create", { error: _error_text(exc) });
    }
    return [null, `Could not create conversation: ${_error_text(exc)}`];
  }
}

/**
 * Run one turn of an OpenAI CONVERSATION (server-maintained history). Creates the conversation
 * if `conversation_id` is null. Only the NEW `user_input` is sent — the server already holds the
 * prior turns. `instructions` is the system guidance, re-sent each turn. Returns
 * (parsed_json, conversation_id, error).
 */
export async function call_openai_conversation_json(
  schema_name: string,
  schema: Record<string, any>,
  instructions: string,
  conversation_id: string | null,
  user_input: string,
  log_slug: string | null = null,
  run_data: Record<string, any> | null = null,
): Promise<[Record<string, any> | null, string | null, string | null]> {
  const api_key = get_openai_api_key();
  const vector_store_id = _get_vector_store_id();
  const settings = load_openai_settings(schema_name);
  const model = settings["model"];
  const reasoning = settings["reasoning"];
  const tools = vector_store_id ? [{ type: "file_search", vector_store_ids: [vector_store_id] }] : [];
  if (log_slug) {
    append_verbose_log(log_slug, `openai.request.${schema_name}`, { model, reasoning, conversation_id, user_input });
  }
  if (!api_key) {
    return [null, conversation_id, "OPENAI_API_KEY is not set."];
  }
  try {
    if (!conversation_id) {
      const conversation = await _post_openai("/v1/conversations", {}, api_key);
      conversation_id = conversation["id"];
    }
    const create_kwargs: Record<string, any> = {
      model,
      conversation: conversation_id,
      instructions,
      input: [{ role: "user", content: user_input }],
      text: { format: { type: "json_schema", name: schema_name, schema, strict: true } },
    };
    if (tools.length) create_kwargs["tools"] = tools;
    if (reasoning) create_kwargs["reasoning"] = reasoning;
    const response = await _post_openai("/v1/responses", create_kwargs, api_key);
    const raw_text = _response_output_text(response);
    const cost = openai_cost_summary(response, model);
    log_openai_cost(run_data, schema_name, cost);
    if (!raw_text) {
      return [null, conversation_id, "OpenAI returned an empty response."];
    }
    const parsed = JSON.parse(raw_text);
    if (log_slug) {
      append_verbose_log(log_slug, `openai.response.${schema_name}`, { conversation_id, parsed_json: parsed, cost });
    }
    return [parsed, conversation_id, null];
  } catch (exc) {
    if (log_slug) {
      append_verbose_log(log_slug, `openai.error.${schema_name}`, { error: _error_text(exc), conversation_id });
    }
    return [null, conversation_id, `OpenAI call failed: ${_error_text(exc)}`];
  }
}

/**
 * Pull the in-progress value of the JSON `"reply"` string out of a partially-streamed JSON buffer.
 * The strict schema emits `reply` first, so the buffer looks like `{"reply":"Hi the…` mid-stream;
 * we read from after `"reply":"` up to the first UNescaped closing quote (or the end of what has
 * arrived). Handles the common JSON escapes so the live text reads cleanly.
 */
export function _extract_partial_reply(buffer: string): string {
  const m = buffer.match(/"reply"\s*:\s*"/);
  if (!m || m.index === undefined) return "";
  let i = m.index + m[0].length;
  let out = "";
  while (i < buffer.length) {
    const ch = buffer[i];
    if (ch === "\\") {
      const next = buffer[i + 1];
      if (next === undefined) break; // escape split across chunks — wait for more
      if (next === "n") out += "\n";
      else if (next === "t") out += "\t";
      else if (next === "r") out += "\r";
      else if (next === "u") {
        const hex = buffer.slice(i + 2, i + 6);
        if (hex.length < 4) break;
        out += String.fromCharCode(parseInt(hex, 16));
        i += 6;
        continue;
      } else out += next; // \" \\ \/ etc.
      i += 2;
      continue;
    }
    if (ch === '"') break; // closing quote → reply is complete
    out += ch;
    i++;
  }
  return out;
}

/**
 * Streaming twin of `call_openai_conversation_json`: runs one conversation turn with `stream: true`
 * and invokes `on_reply_delta` with the growing `reply` text as tokens arrive (ChatGPT-style),
 * then returns the same `[parsed, conversation_id, error]` tuple once the full JSON has landed.
 * Streaming uses the real `fetch` directly (the test seam only exposes `text()`); if `fetch` or a
 * streaming body is unavailable, it transparently falls back to the non-streaming call.
 */
export async function call_openai_conversation_json_streaming(
  schema_name: string,
  schema: Record<string, any>,
  instructions: string,
  conversation_id: string | null,
  user_input: string,
  on_reply_delta: (partial_reply: string) => void,
  log_slug: string | null = null,
  run_data: Record<string, any> | null = null,
): Promise<[Record<string, any> | null, string | null, string | null]> {
  const api_key = get_openai_api_key();
  if (!api_key) return [null, conversation_id, "OPENAI_API_KEY is not set."];
  if (typeof fetch === "undefined") {
    return call_openai_conversation_json(schema_name, schema, instructions, conversation_id, user_input, log_slug, run_data);
  }
  const vector_store_id = _get_vector_store_id();
  const settings = load_openai_settings(schema_name);
  const model = settings["model"];
  const reasoning = settings["reasoning"];
  const tools = vector_store_id ? [{ type: "file_search", vector_store_ids: [vector_store_id] }] : [];
  if (log_slug) {
    append_verbose_log(log_slug, `openai.request.${schema_name}`, { model, reasoning, conversation_id, user_input, stream: true });
  }
  try {
    if (!conversation_id) {
      const conversation = await _post_openai("/v1/conversations", {}, api_key);
      conversation_id = conversation["id"];
    }
    const create_kwargs: Record<string, any> = {
      model,
      conversation: conversation_id,
      instructions,
      input: [{ role: "user", content: user_input }],
      text: { format: { type: "json_schema", name: schema_name, schema, strict: true } },
      stream: true,
    };
    if (tools.length) create_kwargs["tools"] = tools;
    if (reasoning) create_kwargs["reasoning"] = reasoning;

    // Stall protection: a generous overall backstop (_STREAM_MAX_DURATION_MS) plus a
    // read-inactivity watchdog (no chunk for _STREAM_INACTIVITY_TIMEOUT_MS) — a silently hung
    // connection would otherwise stall trait/compare chat forever, while an actively-streaming
    // long turn must NOT be cut off mid-generation. The abort surfaces as a fetch/read error
    // and flows into the existing recovery-by-response-id path below.
    const abort_controller = new AbortController();
    const deadline_timer = setTimeout(() => abort_controller.abort(), _STREAM_MAX_DURATION_MS);
    let inactivity_timer: ReturnType<typeof setTimeout> | undefined;
    const bump_inactivity = () => {
      clearTimeout(inactivity_timer);
      inactivity_timer = setTimeout(() => abort_controller.abort(), _STREAM_INACTIVITY_TIMEOUT_MS);
    };

    let output_text = "";
    let final_response: any = null;
    let response_id: string | null = null;
    let stream_error: string | null = null;

    const process_data = (data: string) => {
      if (!data || data === "[DONE]") return;
      let ev: any;
      try {
        ev = JSON.parse(data);
      } catch {
        return;
      }
      // Any lifecycle event (response.created, .in_progress, .completed …) carries the id —
      // remember it so a broken stream can still retrieve the finished turn.
      if (typeof ev?.response?.id === "string") response_id = ev.response.id;
      if (ev?.type === "response.output_text.delta" && typeof ev.delta === "string") {
        output_text += ev.delta;
        on_reply_delta(_extract_partial_reply(output_text));
      } else if ((ev?.type === "response.completed" || ev?.type === "response.incomplete") && ev.response) {
        final_response = ev.response;
      } else if (ev?.type === "response.failed" && ev.response) {
        stream_error = String(ev.response?.error?.message ?? "The response failed.");
      } else if (ev?.type === "error") {
        stream_error = String(ev.message ?? ev.error?.message ?? "Stream error.");
      }
    };
    const process_event_block = (raw_event: string) => {
      for (const line of raw_event.split("\n")) {
        const trimmed = line.replace(/^\s+/, "");
        if (trimmed.startsWith("data:")) process_data(trimmed.slice(5).trim());
      }
    };

    try {
      const resp = await fetch(`${OPENAI_BASE_URL}/v1/responses`, {
        method: "POST",
        headers: { Authorization: `Bearer ${api_key}`, "Content-Type": "application/json" },
        body: JSON.stringify(create_kwargs),
        signal: abort_controller.signal,
      });
      if (!resp.ok || !resp.body) {
        // Fall back to the non-streaming path so a stream-unfriendly response still works.
        return call_openai_conversation_json(schema_name, schema, instructions, conversation_id, user_input, log_slug, run_data);
      }

      const reader = resp.body.getReader();
      const decoder = new TextDecoder();
      let sse_buffer = "";
      try {
        bump_inactivity();
        for (;;) {
          const { done, value } = await reader.read();
          bump_inactivity();
          if (done) break;
          sse_buffer += decoder.decode(value, { stream: true });
          let sep: number;
          while ((sep = sse_buffer.indexOf("\n\n")) !== -1) {
            const raw_event = sse_buffer.slice(0, sep);
            sse_buffer = sse_buffer.slice(sep + 2);
            process_event_block(raw_event);
          }
        }
        sse_buffer += decoder.decode();
        // Flush a final event that arrived without its trailing blank line (some proxies trim it).
        if (sse_buffer.trim()) process_event_block(sse_buffer);
      } catch (exc) {
        // The CONNECTION broke mid-stream (e.g. "Error in input stream", or a watchdog abort) —
        // don't fail the turn yet; the response usually completed server-side and is
        // recoverable below by id.
        stream_error = _error_text(exc);
      }
    } finally {
      clearTimeout(deadline_timer);
      clearTimeout(inactivity_timer);
    }

    if (!final_response && response_id) {
      // Lost the tail of the feed but know the response id → fetch the finished turn directly.
      final_response = await _recover_streamed_response(response_id, api_key);
      if (final_response && log_slug) {
        append_verbose_log(log_slug, `openai.stream_recovered.${schema_name}`, {
          conversation_id,
          response_id,
          stream_error,
        });
      }
    }
    if (!final_response && stream_error && !output_text) {
      return [null, conversation_id, `OpenAI call failed: ${stream_error}`];
    }

    const raw_text = (final_response ? _response_output_text(final_response) : "") || output_text;
    if (final_response) {
      const cost = openai_cost_summary(final_response, model);
      log_openai_cost(run_data, schema_name, cost);
    }
    if (!raw_text) {
      // Keep the stream diagnostic if we had one (e.g. recovered a response with no text output).
      return [null, conversation_id, stream_error ? `OpenAI call failed: ${stream_error}` : "OpenAI returned an empty response."];
    }
    let parsed: Record<string, any>;
    try {
      parsed = JSON.parse(raw_text);
    } catch {
      // Truncated tail with no recoverable response — surface the stream error honestly.
      return [null, conversation_id, `OpenAI call failed: ${stream_error ?? "incomplete streamed response"}`];
    }
    if (typeof parsed?.["reply"] === "string") on_reply_delta(parsed["reply"]); // settle the final text
    if (log_slug) {
      append_verbose_log(log_slug, `openai.response.${schema_name}`, { conversation_id, parsed_json: parsed, streamed: true });
    }
    return [parsed, conversation_id, null];
  } catch (exc) {
    if (log_slug) {
      append_verbose_log(log_slug, `openai.error.${schema_name}`, { error: _error_text(exc), conversation_id });
    }
    return [null, conversation_id, `OpenAI call failed: ${_error_text(exc)}`];
  }
}

export async function process_user_answers(
  user_profile: Record<string, any>,
  questions: Record<string, any>[],
  answers: Record<string, string>,
  extra_info: string,
  run_data: Record<string, any> | null = null,
): Promise<[Record<string, any> | null, string | null, string | null]> {
  const payload = {
    // Dulled-down birth context: only the birth date — no coordinates, chart, or astrological
    // analysis. The event extractor needs it solely to reject pre-birth or future events.
    birth: { date: user_profile["birth"]?.["date"] ?? "" },
    current_events: compact_events_for_openai(user_profile["events"] ?? []),
    questions,
    answers,
    extra_info,
    supported_event_categories: EVENT_CATEGORIES,
  };
  return call_openai_json(
    "rectification_fact_extraction",
    FACT_EXTRACTION_SCHEMA,
    load_instruction("extraction_fact_event.md"),
    payload,
    user_profile["slug"] ?? null,
    run_data,
  );
}

